import './settlement-preference.scss';

import { updateSettlementPreference } from '@api';
import { Button, Dropdown } from '@basqet-dashboard/common/components';
import {
  resolveInitialSettlementType,
  SETTLEMENT_PREFERENCE_OPTIONS,
  SETTLEMENT_PREFERENCE_TYPES
} from '@basqet-dashboard/common/config';
import { useCurrencies } from '@basqet-dashboard/common/hooks';
import { formatErrorMessage } from '@basqet-dashboard/common/utils';
import { useFetchKeys } from '@hooks/queries';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

// Only NGN is supported on the fiat side today — mirrors the previous
// settlement-currency component. Crypto currencies pass through as-is.
const ALLOWED_FIAT_SETTLEMENT_CURRENCIES = ['NGN'];

const { SPECIFIC_CURRENCY } = SETTLEMENT_PREFERENCE_TYPES;

const SettlementPreference = () => {
  const { keys, refetch: refetchMeta } = useFetchKeys();
  const { cryptoCurrencies, fiatCurrencies } = useCurrencies();

  const [selectedType, setSelectedType] = useState(() => resolveInitialSettlementType(keys));
  const [selectedCurrencyId, setSelectedCurrencyId] = useState(
    () => keys?.settlement_currency || ''
  );
  const [isSaving, setIsSaving] = useState(false);

  // Re-sync local state when /merchant/meta refetches (e.g. after save).
  useEffect(() => {
    setSelectedType(resolveInitialSettlementType(keys));
    setSelectedCurrencyId(keys?.settlement_currency || '');
  }, [keys?.settlement_preference_type, keys?.settlement_currency]);

  const currencyOptions = [
    { value: '', label: '' },
    ...cryptoCurrencies.map(({ slug, value }) => ({ label: slug, value })),
    ...fiatCurrencies
      .filter(({ slug }) => ALLOWED_FIAT_SETTLEMENT_CURRENCIES.includes(slug.toUpperCase()))
      .map(({ slug, value }) => ({ label: slug, value }))
  ];

  const initialType = resolveInitialSettlementType(keys);
  const initialCurrencyId = keys?.settlement_currency || '';
  const isPristine =
    selectedType === initialType &&
    (selectedType !== SPECIFIC_CURRENCY ||
      String(selectedCurrencyId) === String(initialCurrencyId));
  const needsCurrency = selectedType === SPECIFIC_CURRENCY && !selectedCurrencyId;
  const isSaveDisabled = isPristine || needsCurrency || isSaving;

  const handleSave = async () => {
    if (needsCurrency) {
      toast.error('Please select a settlement currency');
      return;
    }

    setIsSaving(true);
    try {
      await updateSettlementPreference({
        type: selectedType,
        currency_id: selectedType === SPECIFIC_CURRENCY ? Number(selectedCurrencyId) : null
      });
      toast.success('Settlement preference updated');
      refetchMeta();
    } catch (error) {
      formatErrorMessage(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card settlements">
      <h3 className="card__header">Settlement settings</h3>

      <div className="settlement-preference" role="radiogroup" aria-label="Settlement preference">
        {SETTLEMENT_PREFERENCE_OPTIONS.map((option) => {
          const isSelected = selectedType === option.value;
          const optionId = `settlement-preference-${option.value}`;
          return (
            <div
              key={option.value}
              className={`settlement-preference__option ${
                isSelected ? 'settlement-preference__option--selected' : ''
              }`}>
              <div className="settlement-preference__option__row">
                <input
                  id={optionId}
                  type="radio"
                  name="settlement-preference"
                  value={option.value}
                  checked={isSelected}
                  onChange={() => setSelectedType(option.value)}
                  className="settlement-preference__option__radio"
                />
                <label htmlFor={optionId} className="settlement-preference__option__label">
                  <span className="settlement-preference__option__title">{option.title}</span>
                  <span className="settlement-preference__option__description">
                    {option.description}
                  </span>
                </label>
              </div>

              {isSelected && option.value === SPECIFIC_CURRENCY && (
                <div className="settlement-preference__currency-picker">
                  <label
                    htmlFor="settlement-preference-currency"
                    className="settlement-preference__currency-picker__label">
                    Settlement currency
                  </label>
                  <Dropdown
                    name="settlement-preference-currency"
                    placeholder="Select currency"
                    options={currencyOptions}
                    defaultValue={selectedCurrencyId ? Number(selectedCurrencyId) : ''}
                    register={{
                      onChange: (event) => setSelectedCurrencyId(event.target.value)
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        className="settlement-preference__save"
        isLoading={isSaving}
        disabled={isSaveDisabled}
        onClick={handleSave}>
        Save changes
      </Button>
    </div>
  );
};

export { SettlementPreference };
